import { openDB } from 'idb';


const DB_NAME = 'image-store';
const IMAGE_STORE = 'images';
const LABEL_STORE = 'labels';


export const getDB = async () => {
    return openDB(DB_NAME, 2, {
        upgrade(db, oldVersion) {
            if (oldVersion < 1) {
                db.createObjectStore(IMAGE_STORE, { keyPath: 'id', autoIncrement: true });
            }
            if (oldVersion < 2) {
                db.createObjectStore(LABEL_STORE, { keyPath: 'id', autoIncrement: true });
            }
        },
    });
};


export const addImageToDB = async (base64String, filename, labelId = null) => {
    const imageSize = await getImageSize(base64String);
    const db = await getDB();
    const tx = db.transaction(IMAGE_STORE, 'readwrite');
    const store = tx.objectStore(IMAGE_STORE);
    const id = await store.add({ src: base64String, filename, imageSize, labelId });
    await tx.done;
    return id;
};

const getImageSize = (base64String) => {
    // console.log('Base64 string:', base64String); // Log the base64 string
    return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
            resolve(`${img.naturalWidth}x${img.naturalHeight}`);
        };

        img.onerror = (error) => {
            reject(error);
        };

        img.src = base64String;
    });
};


export const loadImages = async () => {
    const db = await getDB();
    const tx = db.transaction(IMAGE_STORE, 'readonly');
    const store = tx.objectStore(IMAGE_STORE);
    const images = await store.getAll();
    // Get all labels for lookup
    const labelTx = db.transaction(LABEL_STORE, 'readonly');
    const labelStore = labelTx.objectStore(LABEL_STORE);
    const labels = await labelStore.getAll();
    await labelTx.done;
    // Add labelName to each image and collect those needing DB update
    const imagesToUpdate = [];
    for (const img of images) {
        if (img.labelId != null) {
            const label = labels.find(l => l.id === img.labelId);
            if (label) {
                img.labelName = label.name;
            } else {
                console.warn('Label not found for image:', img);
                img.labelName = null;
                img.labelId = null;
                imagesToUpdate.push({ ...img });
            }
        } else {
            img.labelName = null;
        }
    }
    await tx.done;
    // Update images with invalid labelId in a new transaction
    if (imagesToUpdate.length > 0) {
        const db2 = await getDB();
        const tx2 = db2.transaction(IMAGE_STORE, 'readwrite');
        const store2 = tx2.objectStore(IMAGE_STORE);
        for (const img of imagesToUpdate) {
            await store2.put(img);
        }
        await tx2.done;
    }
    await tx.done;
    return images;
};


// Update (overwrite) an existing image row in place — used for edits that
// replace the stored image entirely (crop/resize save, rename), as opposed
// to setImageLabelId's targeted single-field update below.
export const updateImageInDB = async (image) => {
    const db = await getDB();
    const tx = db.transaction(IMAGE_STORE, 'readwrite');
    const store = tx.objectStore(IMAGE_STORE);
    await store.put(image);
    await tx.done;
};


export const deleteImage = async (id) => {
    const db = await getDB();
    const tx = db.transaction(IMAGE_STORE, 'readwrite');
    const store = tx.objectStore(IMAGE_STORE);
    await store.delete(id);
    await tx.done;
};


export const clearImages = async () => {
    const db = await getDB();
    const tx = db.transaction(IMAGE_STORE, 'readwrite');
    const store = tx.objectStore(IMAGE_STORE);
    await store.clear();
    await tx.done;
};


// Function to fetch an image by filename from the database
export const fetchImageByFilename = async (filename) => {
    const db = await getDB();
    const tx = db.transaction(IMAGE_STORE, 'readonly');
    const store = tx.objectStore(IMAGE_STORE);
    const allImages = await store.getAll();
    const image = allImages.find(image => image.filename === filename);
    await tx.done;
    return image;
};

export const setImageLabelId = async (imageId, labelId) => {
    imageId = parseInt(imageId);
    const db = await getDB();
    const tx = db.transaction(IMAGE_STORE, 'readwrite');
    const store = tx.objectStore(IMAGE_STORE);
    const image = await store.get(imageId);
    if (image) {
        image.labelId = labelId;
        await store.put(image);
    }
    await tx.done;
};

export const copyImage = async (imageId) => {
    const db = await getDB();
    const tx = db.transaction(IMAGE_STORE, 'readwrite');
    const store = tx.objectStore(IMAGE_STORE);
    const image = await store.get(imageId);
    if (image) {
        // Remove id (so IndexedDB can auto-increment) and uuid (so
        // useImageStore backfills a fresh one on next load() — a duplicate
        // must never share its uuid with the original, or reconciliation
        // would collapse both rows onto the same runtime ImageResource).
        const { id, uuid, ...newImage } = image;
        await store.add(newImage);
    }
    await tx.done;
};

// --- LABELS STORE FUNCTIONS ---

export async function addLabel(name, parentId = null) {
    const labels = await getAllLabels();
    if (labels.some(l => l.name === name && l.parentId === parentId)) {
        // throw new Error('A label with this name and parent already exists.');
    }
    const db = await getDB();
    const tx = db.transaction(LABEL_STORE, 'readwrite');
    const store = tx.objectStore(LABEL_STORE);
    const id = await store.add({ name, parentId });
    const newLabel = await store.get(id);
    await tx.done;
    return newLabel;
}

export async function getAllLabels() {
    const db = await getDB();
    const tx = db.transaction(LABEL_STORE, 'readwrite');
    const store = tx.objectStore(LABEL_STORE);
    const labels = await store.getAll();
    // scan all label parentId's for the corresponding node. If no node is found make the parentId null
    labels.forEach(l => {
        if (l.parentId === l.id || (l.parentId !== null && !labels.find(ll => ll.id === l.parentId))) {
            // set the parentid to null and save to the db
            l.parentId = null;
            store.put(l);
        }
    });
    await tx.done;
    return labels;
}

export async function getLabelById(id) {
    id = Number(id);
    const db = await getDB();
    const tx = db.transaction(LABEL_STORE, 'readonly');
    const store = tx.objectStore(LABEL_STORE);
    const req = await store.get(id);
    await tx.done;
    return req;
}

export async function updateLabelParent(labelId, newParentId) {
    labelId = Number(labelId);
    newParentId = Number(newParentId);
    const label = await getLabelById(labelId);
    if (!label) return;
    const db = await getDB();
    const tx = db.transaction(LABEL_STORE, 'readwrite');
    const store = tx.objectStore(LABEL_STORE);
    label.parentId = newParentId;
    const putReq = store.put(label);
    await tx.done;
}

export async function deleteLabelAndChildren(id) {
    id = Number(id);
    const idsToDelete = await collectDescendantIds(id);
    const db = await getDB();
    const tx = db.transaction(LABEL_STORE, 'readwrite');
    const store = tx.objectStore(LABEL_STORE);
    idsToDelete.forEach(labelId => {
        store.delete(labelId);
    });
    await tx.done;
}

export async function collectDescendantIds(id) {
    id = Number(id);
    const labels = await getAllLabels();
    let ids = [id];
    let queue = [id];
    // console.log('labels: ', labels);
    while (queue.length) {
        // console.log('Queue:', queue);
        const currentId = queue.shift();
        // console.log('Current ID:', currentId);
        labels.forEach(l => {
            if (l.parentId === currentId) {
                ids.push(l.id);
                queue.push(l.id);
            }
        });
    }
    return ids;
}

export async function isDescendant(labels, draggedId, targetId) {
    let queue = [targetId];
    while (queue.length) {
        const currentId = queue.shift();
        if (currentId === draggedId) return true;
        labels.forEach(l => {
            if (l.parentId === currentId) queue.push(l.id);
        });
    }
    return false;
}

export async function getLabelPathArray(id) {
    const labels = await getAllLabels();
    const map = {};
    labels.forEach(l => { map[l.id] = l; });
    let path = [];
    let current = map[id];
    while (current) {
        path.unshift(current.name);
        current = current.parentId !== null ? map[current.parentId] : null;
    }
    return path;
}

export async function getLabelPathString(id) {
    const pathArray = await getLabelPathArray(id);
    return pathArray.join('/');
}

export async function getLabelByPathArray(pathArray) {
    let allLabels = await getAllLabels();
    let foundLabel = null;

    // path is like: /level1/level12/level17/level171

    // so we start to get the leve1 root node (no parentid)
    // if it does not exist we create the new rootnode
    // teh we find the level12 node. that is a node with that name and the level1.id as parentid
    let parentId = null;
    for (const segment of pathArray) {
        if (segment === '') continue; // skip empty segments

        allLabels = allLabels.filter(l => l.name === segment && l.parentId === parentId);
        if (allLabels.length === 0) {
            // label does not exist so create a new label
            foundLabel = await addLabel(segment, parentId);
            parentId = foundLabel.id;
        } else {
            // label exists. take the id of the first one as parentid for the next iteration
            parentId = allLabels[0].id;
            foundLabel = allLabels[0];
        }
    }

    return foundLabel;
}
